import { useCallback, useMemo, useState } from 'react';

import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';

export type UseChatUIResponse = {
  drawerCollapsed: boolean;
  toggleDrawerCollapsed: () => void;
  chatOpen: boolean;
  toggleChatOpen: () => void;
  chatExpanded: boolean;
  toggleChatExpanded: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
};

export const useChatUI = () => {
  const [drawerCollapsed, setDrawerCollapsed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [{ chatOpen, chatExpanded }, { updateSearchParams }] = useNavigation({
    chatOpen: SearchParamType.BOOLEAN,
    chatExpanded: SearchParamType.BOOLEAN,
    threadId: SearchParamType.STRING,
    isNewThread: SearchParamType.BOOLEAN,
  });

  const resetState = useCallback(() => {
    updateSearchParams({
      chatOpen: undefined,
      chatExpanded: undefined,
      threadId: undefined,
      isNewThread: undefined,
    });
  }, [updateSearchParams]);

  const toggleDrawerCollapsed = useCallback(() => {
    setDrawerCollapsed(current => !current);
  }, []);

  const toggleChatOpen = useCallback(() => {
    if (!chatOpen) {
      updateSearchParams({ chatOpen: true });
    } else {
      resetState();
    }
  }, [chatOpen, updateSearchParams]);

  const toggleChatExpanded = useCallback(() => {
    updateSearchParams({ chatExpanded: !chatExpanded });
  }, [chatExpanded, updateSearchParams]);

  return useMemo<UseChatUIResponse>(
    () => ({
      drawerCollapsed,
      toggleDrawerCollapsed,
      chatOpen,
      chatExpanded,
      toggleChatOpen,
      toggleChatExpanded,
      loading,
      setLoading,
    }),
    [
      drawerCollapsed,
      toggleDrawerCollapsed,
      chatOpen,
      chatExpanded,
      toggleChatOpen,
      toggleChatExpanded,
      loading,
      setLoading,
    ],
  );
};
